import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, TrendingUp, Pin, Sparkles } from 'lucide-react';
import './landing.css';

/**
 * Concept 5 — CALENDAR.
 * Functionally-first. Lead with a real-looking dividend calendar
 * showing the next 7 days of upcoming pay dates so visitors get
 * IMMEDIATE value before signing in. Below: 3 feature glass-panels.
 * Indigo gradient bg + gold accent — "you came for dividends, here
 * they are."
 */
const UPCOMING = [
  { day: 'Mon', date: '24', ticker: 'O', amount: '$0.262', shares: '120', total: '$31.44' },
  { day: 'Tue', date: '25', ticker: 'JEPI', amount: '$0.447', shares: '85', total: '$38.00' },
  { day: 'Wed', date: '26', ticker: 'SCHD', amount: '$0.751', shares: '210', total: '$157.71' },
  { day: 'Thu', date: '27', ticker: 'MAIN', amount: '$0.245', shares: '60', total: '$14.70', isToday: true },
  { day: 'Fri', date: '28', ticker: 'O', amount: '$0.262', shares: '120', total: '$31.44' },
  { day: 'Sat', date: '01', ticker: null },
  { day: 'Sun', date: '02', ticker: null },
];

export default function Landing5() {
  return (
    <div className="lp5 lp-shell" data-testid="landing-5">
      {/* LandingSwitcher removed Round 91l. */}

      <header className="max-w-6xl mx-auto px-6 pt-24 pb-12">
        <div className="text-xs uppercase tracking-[0.3em] gold font-bold mb-6 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          The next 7 days, exchange-verified
        </div>
        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[0.95] mb-6" data-testid="lp5-title">
          Your dividends.<br />
          <span className="gold">Already scheduled.</span>
        </h1>
        <p className="text-lg text-violet-100/70 max-w-2xl leading-relaxed">
          DiviDrip pulls live exchange data and shows you exactly what&apos;s landing in your brokerage account this week. No estimates. No "approximate". The real dates.
        </p>
      </header>

      {/* Live-looking weekly calendar */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-7 gap-2">
          {UPCOMING.map((c) => (
            <div key={c.date} className={`cal-cell min-h-[170px] flex flex-col ${c.isToday ? 'today' : ''}`}>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.2em] opacity-60">{c.day}</span>
                <span className="text-xl font-bold">{c.date}</span>
              </div>
              {c.ticker ? (
                <>
                  <div className="text-amber-300 font-bold text-lg font-mono">{c.ticker}</div>
                  <div className="text-xs opacity-70 mt-1">{c.amount} × {c.shares}</div>
                  <div className="mt-auto pt-2 border-t border-white/10">
                    <div className="text-xs opacity-60">Payout</div>
                    <div className="gold font-bold text-lg font-mono">{c.total}</div>
                  </div>
                </>
              ) : (
                <div className="mt-auto text-xs opacity-30 italic">No payments</div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs opacity-60 font-mono">
          Sample portfolio — sign in to see yours. <span className="gold">Sat/Sun</span> dates roll to the following Mon (exchange convention).
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-12">
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="lp-btn lp-btn-primary-5" data-testid="lp5-cta-primary">
            Sign in to see your calendar <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="/screener" className="lp-btn border border-amber-400/40 text-amber-200 hover:bg-amber-400/10">
            Browse the dividend universe →
          </a>
        </div>
      </section>

      {/* Glass feature panels */}
      <section className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-3 gap-4">
        <div className="glass p-6">
          <Calendar className="w-7 h-7 gold mb-3" />
          <h3 className="text-xl font-bold mb-2">Locked dates</h3>
          <p className="text-sm text-violet-100/70 leading-relaxed">
            99.7% of dividend-paying tickers carry exchange-verified ex-div and pay dates.
          </p>
        </div>
        <div className="glass p-6">
          <TrendingUp className="w-7 h-7 gold mb-3" />
          <h3 className="text-xl font-bold mb-2">Live moves</h3>
          <p className="text-sm text-violet-100/70 leading-relaxed">
            Top gainers, top losers, real-time price ticks via WebSocket. Sort by % move, volume, or yield — your call.
          </p>
        </div>
        <div className="glass p-6">
          <Pin className="w-7 h-7 gold mb-3" />
          <h3 className="text-xl font-bold mb-2">Pinned compares</h3>
          <p className="text-sm text-violet-100/70 leading-relaxed">
            Save the face-offs you care about. JEPI vs JEPQ. NATH vs PEB. Each pin carries a memo, sortable by yield gap.
          </p>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-12 border-t border-white/10 text-xs text-violet-200/40 flex justify-between flex-wrap gap-3 font-mono">
        <span>DIVIDRIP · 2026 · Data via Massive</span>
        <span><a href="/privacy-policy" className="hover:text-amber-300">Privacy</a> · <a href="/glossary" className="hover:text-amber-300">Glossary</a></span>
      </footer>
    </div>
  );
}
